import { Component, Input, ViewChild, ElementRef, OnInit, forwardRef, Output, EventEmitter } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';



@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true
    }
  ]
})
export class FileUploadComponent implements OnInit,ControlValueAccessor  {
  @Input() label: string = 'Document';  
  @Input() placeholderText: string = 'Click or drag the file here to upload';
  @Input() multiple: boolean = true;     
  @Input() inputId: string = 'document-upload-input'; 
  @Input() isRequired: boolean = false;  // New input for required field
  @Input() formControl!: FormControl;    // Accept FormControl for validation
  @ViewChild('fileInput') fileInput!: ElementRef;  
  @Input() disabled: boolean = false; // Add disabled input
  @Input() maxFiles: number = 10; // Maximum number of files allowed
  @Output() fileUploaded = new EventEmitter<{ name: string, file: File, fileType: string }>();
  @Output() fileRemoved = new EventEmitter<string>();  // Emit the name of the removed file
  @Output() allFilesUploaded = new EventEmitter<void>(); // New event emitter for when all files are uploaded

constructor(
  private toastr: ToastrService
) {

}

  uploadedFiles: File[] = [];
  private onChange = (value: any) => {};
  private onTouched = () => {};
  

  ngOnInit() {

    // Check if formControl is initialized
    if (this.formControl) {
      if (this.isRequired) {
        this.formControl.setValidators(Validators.required);
        this.formControl.updateValueAndValidity();
      }
      if (this.disabled) {
        this.formControl.disable(); // Disable form control if disabled
      }
    } else {
      console.error('FormControl is not initialized');
    }

  }

 

  // Method to expose uploaded files array
  getUploadedFiles(): File[] {
    return this.uploadedFiles;
  }

  onFileUpload(file: File) {
    this.formControl.setValue(file);
    this.formControl.markAsTouched();
    this.formControl.updateValueAndValidity();
  }


  writeValue(value: any): void {
    // Handle initial value
    if (value) {
      // Process the value and update UI if needed
    }
  }


  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;  // Store the function
  }
  
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;  // Store the function
  }
 
  setDisabledState?(isDisabled: boolean): void {
    if (this.formControl) {
      if (isDisabled && !this.formControl.disabled) {
        this.formControl.disable({ emitEvent: false });  // Avoid emitting events to prevent recursion
      } else if (!isDisabled && this.formControl.disabled) {
        this.formControl.enable({ emitEvent: false });  // Same here to avoid triggering changes unnecessarily
      }
    }
  }

// Event handler for when a file is selected via click
// Add this property in your component to track file upload count
private filesProcessedCount = 0;

onFileSelected(event: any) {
  const files: FileList = event.target.files;
  const maxSizeInMB = 5;
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

  const allowedMimeTypes = ['application/pdf', 'application/msword', 
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 
                            'image/png', 'image/jpeg', 'image/gif',
                            'application/vnd.ms-outlook', // .msg files
                            'application/vnd.ms-project', // .mpp files
                            'application/vnd.google-earth.kmz', // .kmz files
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']; // .xlsx files

  if (files.length === 0) {
    return;
  }

  // If multiple files are allowed, process them as an array
  if (this.multiple) {
    // Check if the selected files exceed the maximum allowed number of files
    if ((this.uploadedFiles.length + files.length) > this.maxFiles) {
      this.toastr.error(`You can only upload a maximum of ${this.maxFiles} files.`);
      return;
    }

    // Array to collect promises for the Base64 conversion
    const fileUploadPromises: Promise<void>[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file size
      if (file.size > maxSizeInBytes) {
        this.toastr.error(`The file ${file.name} exceeds the maximum size of ${maxSizeInMB} MB.`);
        continue;
      }

      // Validate file type (check both MIME type and file extension as fallback)
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
      const allowedExtensions = ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg', 'gif'];
      const isValidMimeType = allowedMimeTypes.includes(file.type);
      const isValidExtension = allowedExtensions.includes(fileExtension);
      
      if (!isValidMimeType && !isValidExtension) {
        this.toastr.error(`The file ${file.name} is not a valid document or image. Allowed types: PDF, DOC, DOCX, PNG, JPG, JPEG, GIF, MSG, MPP, KMZ, XLSX`);
        continue;
      }

      // Only proceed with the file if it passes validation
      if (!this.uploadedFiles.some(f => f.name === file.name && f.size === file.size)) {
        this.uploadedFiles.push(file);

        // Convert the file to Base64 and store the promise in fileUploadPromises
        const fileUploadPromise = this.convertFileToBase64(file).then(base64 => {
          const fileData = {
            base64,
            name: file.name,
            extension: file.name.split('.').pop() || '',
            mimeType: file.type
          };

          // Emit the fileUploaded event after processing
          this.fileUploaded.emit({ name: file.name, file, fileType: file.type });

          // Set the value for the form control
          this.formControl.setValue(`${fileData.name}|${fileData.extension}|${fileData.mimeType}|${fileData.base64}`);
          this.formControl.markAsTouched();
          this.formControl.markAsDirty();
          this.formControl.updateValueAndValidity();  // Trigger re-validation
        });

        // Push the promise to the array
        fileUploadPromises.push(fileUploadPromise);
      }
    }

    // Wait for all the Base64 conversions to complete
    Promise.all(fileUploadPromises).then(() => {
      this.allFilesUploaded.emit();  // All files have been processed
    }).catch(error => {
      console.error('Error processing files:', error);
    });

  } else {
    // Handle single file upload
    const file = files[0];

    // Validate file size
    if (file.size > maxSizeInBytes) {
      this.toastr.error(`The file ${file.name} exceeds the maximum size of ${maxSizeInMB} MB.`);
      return;
    }

    // Validate file type (check both MIME type and file extension as fallback)
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
    const allowedExtensions = ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg', 'gif'];
    const isValidMimeType = allowedMimeTypes.includes(file.type);
    const isValidExtension = allowedExtensions.includes(fileExtension);
    
    if (!isValidMimeType && !isValidExtension) {
      this.toastr.error(`The file ${file.name} is not a valid document or image. Allowed types: PDF, DOC, DOCX, PNG, JPG, JPEG, GIF, MSG, MPP, KMZ, XLSX`);
      return;
    }

    // Remove the previous file if exists (for single file upload)
    if (this.uploadedFiles.length > 0) {
      this.removeFile(this.uploadedFiles[0]);  // Assuming only one file is allowed
    }

    this.uploadedFiles = [file];  // Replace with the new file

    // Convert the single file to Base64
    this.convertFileToBase64(file).then(base64 => {
      const fileData = {
        base64,
        name: file.name,
        extension: file.name.split('.').pop() || '',
        mimeType: file.type
      };

      // Emit the fileUploaded event after processing
      this.fileUploaded.emit({ name: file.name, file, fileType: file.type });

      // Set the value for the form control
      this.formControl.setValue(`${fileData.name}|${fileData.extension}|${fileData.mimeType}|${fileData.base64}`);
      this.formControl.markAsTouched();
      this.formControl.markAsDirty();
      this.formControl.updateValueAndValidity();  // Trigger re-validation

      // All files processed
      this.allFilesUploaded.emit();
    }).catch(error => {
      console.error('Error processing file:', error);
    });
  }
}





// Method to push a file to the uploadedFiles array and update the form control
pushFileToUploadedFiles(fileName: string, fileContent: Blob, fileType: string) {
  // Create a new File object
  const newFile = new File([fileContent], fileName, { type: fileType });

  // Add or replace the file in the uploadedFiles array
  if (this.multiple) {
    if (!this.uploadedFiles.some(f => f.name === newFile.name && f.size === newFile.size)) {
      this.uploadedFiles.push(newFile);


    }
  } else {
    this.uploadedFiles = [newFile];
  }

  // Convert all files to Base64 and update form control
  const filePromises: Promise<{ base64: string, name: string, extension: string, mimeType: string }>[] = this.uploadedFiles.map(file =>
    this.convertFileToBase64(file).then(base64 => ({
      base64,
      name: file.name,
      extension: file.name.split('.').pop() || '',
      mimeType: file.type
    }))
  );

  Promise.all(filePromises).then(fileDataArray => {
    // Debugging: Log the file data
    //console.log('File data array:', fileDataArray);

    // Join base64 strings with metadata (e.g., semicolon-separated)
    const formData = fileDataArray.map(fileData =>
      `${fileData.name}|${fileData.extension}|${fileData.mimeType}|${fileData.base64}`
    ).join(';'); // Use a semicolon to separate file entries

   // console.log('FormData to set:', formData); // Debugging: Log the final formData

    this.formControl.setValue(formData);
    this.formControl.markAsTouched();
    this.formControl.markAsDirty();
    this.formControl.updateValueAndValidity(); // Trigger re-validation
  });
}

  clearUploadedFiles() {
    this.uploadedFiles = [];  // Clear the array
    this.formControl.setValue(null);  // Optionally clear the FormControl value
  }

  // Trigger file input dialog programmatically
  triggerFileInput(event: MouseEvent) {
    if (this.disabled) {
      return;
    }
    event.preventDefault();  
    event.stopPropagation();  
    this.fileInput.nativeElement.click();  
  }

  // Drag over event
  onDragOver(event: DragEvent) {
    if (this.disabled) {
      return;
    }
    event.preventDefault();  
    event.stopPropagation();
  }

  // Drag leave event
  onDragLeave(event: DragEvent) {
    if (this.disabled) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
  }

// Drop event for drag-and-drop
onDrop(event: DragEvent) {
  if (this.disabled) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();


  if (event.dataTransfer && event.dataTransfer.files.length > 0) {
    // Directly pass the files from drag-and-drop to onFileSelected
    const fileEvent = {
      target: {
        files: event.dataTransfer.files
      }
    };
    this.onFileSelected(fileEvent); // Reuse the logic in onFileSelected

    event.dataTransfer.clearData(); // Clear the drag-and-drop data
  }
}


  private base64ToBlob(base64: string, contentType: string = ''): Blob {
    const byteCharacters = atob(base64);  // Decode Base64
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });  // Create Blob from byte array
  }
  
  convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1]; // Extract base64 part
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  previewFile(file: File | string) {
    if (file instanceof File) {
      // Handle regular File object (e.g., from an input type="file")
      const fileURL = URL.createObjectURL(file);  // Create a URL for the File object
      window.open(fileURL);  // Open the File in a new window
    } else if (typeof file === 'string' && file.startsWith('data:')) {
      // Handle Base64 string (e.g., data:image/png;base64,...)
      const newWindow = window.open();  // Open a new window
      if (newWindow) {
        newWindow.document.write(`<img src="${file}" alt="Base64 Image">`);
        newWindow.document.close();  // Close document construction
      } else {
        console.error('Failed to open a new window');
      }
    } else {
      console.error('The provided input is not a valid File object or Base64 string');
    }
  }


  removeFile(file: File) {
    if (this.disabled) {
      return;
    }
  
    const index = this.uploadedFiles.indexOf(file);
  
    if (index > -1) {
      this.uploadedFiles.splice(index, 1); // Remove the file from the array
  
      // Convert remaining files to Base64 and update form control
      const filePromises: Promise<{ base64: string, name: string, extension: string, mimeType: string }>[] = this.uploadedFiles.map(file =>
        this.convertFileToBase64(file).then(base64 => ({
          base64,
          name: file.name,
          extension: file.name.split('.').pop() || '',
          mimeType: file.type
        }))
      );
  
      Promise.all(filePromises).then(fileDataArray => {
        // Join base64 strings with metadata (e.g., semicolon-separated)
        const formData = fileDataArray.map(fileData =>
          `${fileData.name}|${fileData.extension}|${fileData.mimeType}|${fileData.base64}`
        ).join(';'); // Use a semicolon to separate file entries
  
        this.formControl.setValue(formData);
        this.formControl.markAsTouched();
        this.formControl.markAsDirty();
        this.formControl.updateValueAndValidity(); // Trigger re-validation
        this.fileRemoved.emit(file.name);  // Emit the file name
      });
  
      // Reset the file input so the same file can be selected again
      this.fileInput.nativeElement.value = ''; // Clear the file input element
  
    } else {
      // If no files are left, clear the form control
      this.formControl.setValue(''); // Clear the form control (or use null if required)
      this.formControl.markAsTouched(); // Mark as touched to trigger validation
      this.formControl.markAsDirty(); // Ensure the form control is marked as dirty
      this.formControl.updateValueAndValidity(); // Trigger re-validation
      this.fileRemoved.emit('');
  
      // Reset the file input so the same file can be selected again
      this.fileInput.nativeElement.value = ''; // Clear the file input element
    }
  }
  


  // Helper method to format file size
  getFileSize(size: number): string {
    const mbSize = size / (1024 * 1024); 
    return `${mbSize.toFixed(4)} MB`;
  }

  // Method to convert a file to Base64
  // private convertFileToBase64(file: File): Promise<string | ArrayBuffer | null> {
  //   return new Promise((resolve, reject) => {
  //     const reader = new FileReader();
  //     reader.readAsDataURL(file);
  //     reader.onload = () => resolve(reader.result);
  //     reader.onerror = error => reject(error);
  //   });
  // }

  // Method to upload files
  uploadFiles() {
    if (this.uploadedFiles.length > 0) {
      console.log('Uploading files:', this.uploadedFiles); 
    } else {
      console.log('No files selected for upload.');
    }
  }
}
